export const calculateTotalRow = (data, totals) => {
    if (!data || data.length === 0) return null;

    const safeDiv = (a, b) => (b ? a / b : 0);

    const sums = {
        TotRooms: 0,
        TransRooms: 0,
        GrpRooms: 0,
        CurrRoomsSold: 0,
        CurrRoomRev: 0,
        PickUpRooms: 0,
        PickUpRoomRev: 0,
        BudRoomsSold: 0,
        BudRoomRev: 0,
        LYRoomsSold: 0,
        LYRoomRev: 0,
        RoomsSoldVar: 0,
        RoomRevVar: 0,
        LYRoomsSoldVar: 0,
        LYRoomRevVar: 0,
    };

    let totalPickUpADR = 0;
    let countPickUpADR = 0;

    data.forEach(row => {
        sums.TotRooms += parseFloat(row.TotRooms) || 0;
        sums.TransRooms += parseFloat(row.TransRooms) || 0;
        sums.GrpRooms += parseFloat(row.GrpRooms) || 0;
        sums.CurrRoomsSold += parseFloat(row.CurrRoomsSold) || 0;
        sums.CurrRoomRev += parseFloat(row.CurrRoomRev) || 0;
        const rowPickUpRooms = parseFloat(row.PickUpRooms) || 0;
        const rowPickUpRoomRev = parseFloat(row.PickUpRoomRev) || 0;
        const rowPickUpADR = parseFloat(row.PickUpADR);
        sums.PickUpRooms += rowPickUpRooms;
        sums.PickUpRoomRev += rowPickUpRoomRev;
        if (!Number.isNaN(rowPickUpADR)) {
            totalPickUpADR += rowPickUpADR;
            countPickUpADR++;
        }
        sums.BudRoomsSold += parseFloat(row.BudRoomsSold) || 0;
        sums.BudRoomRev += parseFloat(row.BudRoomRev) || 0;
        sums.LYRoomsSold += parseFloat(row.LYRoomsSold) || 0;
        sums.LYRoomRev += parseFloat(row.LYRoomRev) || 0;
        sums.RoomsSoldVar += parseFloat(row.RoomsSoldVar) || 0;
        sums.RoomRevVar += parseFloat(row.RoomRevVar) || 0;
        sums.LYRoomsSoldVar += parseFloat(row.LYRoomsSoldVar) || 0;
        sums.LYRoomRevVar += parseFloat(row.LYRoomRevVar) || 0;
    });

    // Helper to find a total field from totals or the first data row
    const findTotalVal = (...keys) => {
        const sources = [totals, data && data[0]];
        for (const src of sources) {
            if (!src) continue;
            for (const key of keys) {
                if (src[key] !== undefined && src[key] !== null && src[key] !== '') {
                    const num = parseFloat(src[key]);
                    if (!isNaN(num)) return num;
                }
            }
        }
        return undefined;
    };

    // 1. Details & Room Counts (Use API totals if present, else keep accumulated sums)
    const totalRoomsAvail = findTotalVal("TotalRoomsAvailable", "TotalTotRooms");
    if (totalRoomsAvail !== undefined) sums.TotRooms = totalRoomsAvail;

    const transRooms = findTotalVal("TotalTransRooms");
    if (transRooms !== undefined) sums.TransRooms = transRooms;

    const grpRooms = findTotalVal("TotalGrpRooms");
    if (grpRooms !== undefined) sums.GrpRooms = grpRooms;

    // 2. OTB Actuals
    const actualRoomsSold = findTotalVal("TotalActualRoomsSold", "TotalOTBRoomsSold");
    if (actualRoomsSold !== undefined) sums.CurrRoomsSold = actualRoomsSold;

    const actualRoomRev = findTotalVal("TotalActualRoomRevenue", "TotalOTBRoomRevenue");
    if (actualRoomRev !== undefined) sums.CurrRoomRev = actualRoomRev;

    const currOcc = findTotalVal("TotalActualOccupancy", "TotalOTBOccupancy", "TotalActualOcc")
        ?? (safeDiv(sums.CurrRoomsSold, sums.TotRooms) * 100);

    const currADR = findTotalVal("TotalActualADR", "TotalOTBADR")
        ?? safeDiv(sums.CurrRoomRev, sums.CurrRoomsSold);

    const currRevPar = findTotalVal("TotalActualRevPar", "TotalOTBRevPar")
        ?? safeDiv(sums.CurrRoomRev, sums.TotRooms);

    // 3. Pickup
    const pickUpRooms = findTotalVal("TotalPickUpRooms");
    if (pickUpRooms !== undefined) sums.PickUpRooms = pickUpRooms;

    const pickUpRoomRev = findTotalVal("TotalPickUpRoomRevenue");
    if (pickUpRoomRev !== undefined) sums.PickUpRoomRev = pickUpRoomRev;

    const pickUpADR = findTotalVal("TotalPickUpADR")
        ?? safeDiv(sums.PickUpRoomRev, sums.PickUpRooms);

    const pickUpOcc = findTotalVal("TotalPickUpOcc", "TotalPickUpOccupancy")
        ?? (safeDiv(sums.PickUpRooms, sums.TotRooms) * 100);

    // 4. Budget
    const budRoomsSold = findTotalVal("TotalBudgetRoomsSold", "TotalBudRoomsSold");
    if (budRoomsSold !== undefined) sums.BudRoomsSold = budRoomsSold;

    const budRoomRev = findTotalVal("TotalBudgetRoomRevenue", "TotalBudRoomRev");
    if (budRoomRev !== undefined) sums.BudRoomRev = budRoomRev;

    const budCapacity = findTotalVal("TotalBudRoomsAvailable", "TotalBudgetRoomsAvailable")
        ?? sums.TotRooms;

    const budOcc = findTotalVal("TotalBudgetOccupancy", "TotalBudOcc")
        ?? (safeDiv(sums.BudRoomsSold, budCapacity) * 100);

    const budADR = findTotalVal("TotalBudgetADR", "TotalBudADR")
        ?? safeDiv(sums.BudRoomRev, sums.BudRoomsSold);

    const budRevPar = findTotalVal("TotalBudgetRevPar", "TotalBudRevPar")
        ?? safeDiv(sums.BudRoomRev, budCapacity);

    // 5. LY Actuals
    const lyRoomsSold = findTotalVal("TotalLYRoomsSold");
    if (lyRoomsSold !== undefined) sums.LYRoomsSold = lyRoomsSold;

    const lyRoomRev = findTotalVal("TotalLYRoomRevenue");
    if (lyRoomRev !== undefined) sums.LYRoomRev = lyRoomRev;

    const lyCapacity = findTotalVal("TotalLYRoomsAvailable")
        ?? sums.TotRooms;

    const lyOcc = findTotalVal("TotalLYOccupancy", "TotalLYOcc")
        ?? (safeDiv(sums.LYRoomsSold, lyCapacity) * 100);

    const lyADR = findTotalVal("TotalLYADR", "TotalLYAdr")
        ?? safeDiv(sums.LYRoomRev, sums.LYRoomsSold);

    const lyRevPar = findTotalVal("TotalLYRevPar", "TotalLYRevpar")
        ?? safeDiv(sums.LYRoomRev, lyCapacity);

    // 6. Variances (Use API variance totals if available; otherwise calculate variance from totals)
    const roomsSoldVar = findTotalVal("TotalRoomsSoldVar", "TotalRoomsSoldVariance")
        ?? (sums.CurrRoomsSold - sums.BudRoomsSold);

    const roomRevVar = findTotalVal("TotalRoomRevVar", "TotalRoomRevenueVariance")
        ?? (sums.CurrRoomRev - sums.BudRoomRev);

    const occVar = findTotalVal("TotalOccVar", "TotalOccVariance")
        ?? (currOcc - budOcc);

    const adrVar = findTotalVal("TotalADRVar", "TotalADRVariance")
        ?? (currADR - budADR);

    const lyRoomsSoldVar = findTotalVal("TotalLYRoomsSoldVar", "TotalLYRoomsSoldVariance")
        ?? (sums.CurrRoomsSold - sums.LYRoomsSold);

    const lyRoomRevVar = findTotalVal("TotalLYRoomRevVar", "TotalLYRoomRevenueVariance")
        ?? (sums.CurrRoomRev - sums.LYRoomRev);

    const lyOccVar = findTotalVal("TotalLYOccVar", "TotalLYOccVariance")
        ?? (currOcc - lyOcc);

    const lyADRVar = findTotalVal("TotalLYADRVar", "TotalLYADRVariance")
        ?? (currADR - lyADR);

    return {
        ...sums,
        CurrOcc: currOcc,
        CurrADR: currADR,
        CurrRevPar: currRevPar,

        PickUpADR: pickUpADR,
        PickUpOcc: pickUpOcc,

        BudOcc: budOcc,
        BudADR: budADR,
        BudRevPar: budRevPar,

        LYOcc: lyOcc,
        LYADR: lyADR,
        LYRevPar: lyRevPar,

        RoomsSoldVar: roomsSoldVar,
        RoomRevVar: roomRevVar,
        OccVar: occVar,
        ADRVar: adrVar,
        LYRoomsSoldVar: lyRoomsSoldVar,
        LYRoomRevVar: lyRoomRevVar,
        LYOccVar: lyOccVar,
        LYADRVar: lyADRVar,
    };
};
